import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class PharmacyCartFeedback extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, options);
    document.addEventListener('click', this.openCart, options);
    document.addEventListener('theme-drawer:open', event => {
      if (event.target.id === 'cart-drawer') this.dismiss();
    }, options);
    this.querySelector('[data-cart-feedback-close]')?.addEventListener('click', () => this.dismiss(true), options);
    this.addEventListener('mouseenter', () => clearTimeout(this.timer), options);
    this.addEventListener('mouseleave', () => this.scheduleDismiss(), options);
    this.addEventListener('focusin', () => clearTimeout(this.timer), options);
    this.addEventListener('focusout', () => this.scheduleDismiss(), options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.dismiss(true);
    }, options);
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.dismiss();
    }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.timer);
  }

  async added(result, variantId, quantity, target) {
    this.show(this.dataset.addedMessage);
    try {
      const response = await fetch(this.dataset.cartUrl + '.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: AbortSignal.timeout(8000),
      });
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();
      if (!Number.isSafeInteger(cart.item_count) || !Array.isArray(cart.items)) throw new Error('Invalid cart');
      const update = new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            sections: result.sections,
            source: 'pharmacy-purchase-controls',
            didError: false,
          },
        }),
      });
      (target?.isConnected ? target : document).dispatchEvent(update);
    } catch (_) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer) drawer.dataset.pharmacyCartStale = 'true';
      this.show(this.dataset.addedRefresh);
    }
  }

  onCartUpdate = event => {
    event.promise?.then(({ cart, detail }) => {
      if (cart && Number.isSafeInteger(cart.totalQuantity)) {
        const drawer = document.getElementById('cart-drawer');
        if (drawer) delete drawer.dataset.pharmacyCartStale;
      }
      if (event.action === 'add' && !detail?.didError) this.show(this.dataset.addedMessage);
    }).catch(() => {});
  };

  show(message) {
    if (!this.isConnected || document.getElementById('cart-drawer')?.hasAttribute('open')) return;
    if (!this.contains(document.activeElement)) this.previousFocus = document.activeElement;
    this.querySelector('[data-cart-feedback-message]').textContent = message;
    this.hidden = false;
    this.scheduleDismiss();
  }

  scheduleDismiss() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.matches(':hover')) this.dismiss();
    }, 6000);
  }

  dismiss(restoreFocus = false) {
    clearTimeout(this.timer);
    this.hidden = true;
    if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
  }

  openCart = event => {
    const link = event.target.closest?.('[data-header-cart], [data-cart-feedback-open]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.getElementById('cart-drawer');
    if (typeof drawer?.open !== 'function' || drawer.dataset.pharmacyCartStale === 'true') return;
    event.preventDefault();
    if (this.contains(link)) this.dismiss(true);
    else this.dismiss();
    drawer.open();
  };
}

if (!customElements.get('pharmacy-cart-feedback')) {
  customElements.define('pharmacy-cart-feedback', PharmacyCartFeedback);
}
